import { loaderMarkup } from "../components";
import { appContent } from "../content";
import { mobileWorkspaceNav } from "../layout";
import { filtersHidingRecord, groupsToExpand, manualOverridesAfterQueryChange, visibleItemCountsByGroup } from "../library-menu-state";
import type { AssetDetail, ClauseAsset, ClauseDetail, ContentReportIssueType, NavGroup, NavRecord, RecordKind } from "../types";
import { captionWithoutIdentifier, escapeHtml } from "../utils";
import type { ImageSize } from "./library-capture";
import { cappedCaptureScale, captureFitScale, captureHiddenSelectors, fitScaleFor, fittedStackHeight } from "./library-capture";

/** Intrinsic size the browser knows for an image, which is zero until it decodes. */
const naturalSize = (image: HTMLImageElement): ImageSize => ({ width: image.naturalWidth, height: image.naturalHeight });

export type LibrarySelection = { kind: "clause" | "asset"; id: string };

export type LibraryScreenOptions = {
  selected?: LibrarySelection;
  renderWorkspace: (content: string) => void;
  bindWorkspaceNavigation: () => void;
  openSearch: (prefillQuery?: string) => void;
  saveAnswer: (question: string, summary: string, clauses: string[]) => void;
  openSavedAnswers: () => void;
  listNavigation: () => Promise<{ records: NavRecord[]; groups: NavGroup[] }>;
  filterNavigation: (q: string) => Promise<{ ids: string[] }>;
  getClause: (id: string) => Promise<ClauseDetail>;
  getAsset: (id: string) => Promise<AssetDetail>;
  copyClause: (id: string, format: "text" | "markdown") => Promise<{ content: string }>;
  screenGrab: (recordIds: string[]) => Promise<{ stamp: string }>;
  reportRecord: (report: { record_id: string; record_kind: "clause" | "figure" | "table"; issue_type: ContentReportIssueType; note: string }) => Promise<{ ok: true }>;
  setLocation: (selection: LibrarySelection) => void;
};

type ChipFilter = "all" | "clause" | "figure" | "table";
type DetailTarget = "main" | "subview";

// The menu is built once and kept. Selecting a record must not rebuild it —
// expanded groups stay expanded, scroll position holds, and the current item
// stays highlighted (design §3.2). That is the one real defect in the offline
// browser, whose render() rebuilds the tree on every select(), so this state
// deliberately outlives any single call to renderLibraryScreen.
type LibraryView = {
  root: HTMLElement;
  options: LibraryScreenOptions;
  records: NavRecord[];
  groups: NavGroup[];
  byId: Map<string, NavRecord>;
  expanded: Set<string>;
  manuallyToggled: Set<string>;
  chip: ChipFilter;
  query: string;
  matchedIds: Set<string> | null;
  filterError: string | null;
  selectedId: string | null;
  listScrollTop: number;
  menuRendered: boolean;
  // The newest explicitly requested record, held so a re-entry that lands while
  // /api/navigation is still in flight is not overwritten by the target the
  // original call closed over. Only ever set while `navigationLoaded` is false,
  // and consumed exactly once by the mount callback.
  pendingSelection: LibrarySelection | null;
  navigationLoaded: boolean;
};

type LibraryMenuSessionState = Pick<LibraryView, "chip" | "query" | "selectedId" | "listScrollTop"> & {
  expanded: Set<string>;
  manuallyToggled: Set<string>;
  matchedIds: Set<string> | null;
};

let view: LibraryView | null = null;
let menuSessionState: LibraryMenuSessionState | null = null;
let detailVersion = 0;
let subviewVersion = 0;
let filterVersion = 0;
let filterTimer: number | undefined;
let libraryKeydownController: AbortController | null = null;
const assetImageCleanups = new Map<HTMLElement, Array<() => void>>();
let activeLightbox: { close: () => void; source: HTMLElement } | null = null;
let subviewHistory: LibrarySelection[] = [];
let subviewIndex = -1;
let subviewReturnFocus: HTMLElement | null = null;
let subviewMainScrollTop = 0;

const ASSET_ZOOM_STEP = 1.25;
const ASSET_MAX_SCALE = 4;

function isMounted(): boolean {
  return Boolean(view && document.body.contains(view.root));
}

function assetKind(kind: RecordKind): boolean {
  return kind !== "clause";
}

function selectionFor(record: NavRecord): LibrarySelection {
  return { kind: assetKind(record.kind) ? "asset" : "clause", id: record.id };
}

/** Records passing the chips and the text filter, in document order. */
function visibleRecords(): NavRecord[] {
  if (!view) return [];
  return view.records.filter((record) => {
    if (view!.chip === "clause" && record.kind !== "clause") return false;
    if (view!.chip === "figure" && record.kind !== "figure") return false;
    if (view!.chip === "table" && record.kind !== "table") return false;
    return !view!.matchedIds || view!.matchedIds.has(record.id);
  });
}

function displayedGroups(records = visibleRecords()): Set<string> {
  if (!view) return new Set();
  return groupsToExpand({
    groups: view.groups,
    visibleCountByGroup: visibleItemCountsByGroup(view.groups, records),
    query: view.query,
    expanded: view.expanded,
    manuallyToggled: view.manuallyToggled
  });
}

/**
 * Clears whatever would hide an explicitly requested record, so navigation can
 * always show its destination. Called from every entry point that targets a
 * specific record — mount, re-entry while already mounted — because patching
 * one route at a time is how a filtered-out destination keeps coming back.
 * Returns true when something was cleared.
 */
function revealRecordInMenu(record: NavRecord | undefined): boolean {
  if (!view || !record) return false;
  const hiding = filtersHidingRecord({ chip: view.chip, query: view.query, matchedIds: view.matchedIds }, record);
  if (hiding.chip) {
    view.chip = "all";
    view.root.querySelectorAll<HTMLButtonElement>(".library-chip").forEach((chip) => {
      chip.setAttribute("aria-pressed", String(chip.dataset.chip === view!.chip));
    });
  }
  if (hiding.query) {
    // Drop the search outright rather than re-running it: a pending or failed
    // query could hide the record again moments after the reader arrives.
    window.clearTimeout(filterTimer);
    filterVersion += 1;
    view.query = "";
    view.matchedIds = null;
    view.filterError = null;
    view.manuallyToggled = new Set();
    const filterInput = view.root.querySelector<HTMLInputElement>("#clauseFilter");
    if (filterInput) filterInput.value = "";
  }
  return hiding.chip || hiding.query;
}

// ------------------------------------------------------------------- markup

function shellMarkup(): string {
  const copy = appContent.library;
  return `
    <section class="library-workspace workspace-panel">
      <div class="workspace-header">
        <div>
          <h1>${copy.heading}</h1>
          <p class="muted">${copy.body}</p>
        </div>
        <button id="libraryAskButton" class="secondary" type="button" disabled>${copy.askFromClause}</button>
      </div>
      <div class="library-grid">
        <div class="clause-list" role="navigation" aria-label="${copy.listLabel}">
          <input id="clauseFilter" type="search" placeholder="${copy.filterPlaceholder}" />
          <div class="library-chips" role="group" aria-label="${copy.chipsLabel}">
            ${(["all", "clause", "figure", "table"] as ChipFilter[]).map((chip) => `<button type="button" class="library-chip" data-chip="${chip}" aria-pressed="${chip === "all"}">${copy.chips[chip]}</button>`).join("")}
          </div>
          <p class="library-count" id="libraryCount"></p>
          <div class="clause-list-items">${loaderMarkup("thinking", copy.loading)}</div>
        </div>
        <div class="clause-detail-column">
          <article class="clause-detail" tabindex="-1">${loaderMarkup("thinking", copy.loadingDetail)}</article>
          <section class="reference-subview" aria-label="${copy.referenceSubviewLabel}" tabindex="-1" hidden></section>
        </div>
      </div>
      ${mobileWorkspaceNav("library")}
    </section>
  `;
}

function itemMarkup(record: NavRecord, selectedId: string | null): string {
  const copy = appContent.library;
  const title = assetKind(record.kind)
    ? captionWithoutIdentifier(record.number, record.title)
    : record.title || "";
  const badge = assetKind(record.kind) ? `<span class="record-badge record-badge--${record.kind}">${copy.kindLabels[record.kind] || record.kind}</span>` : "";
  const missing = assetKind(record.kind) && record.isAvailable === false ? `<span class="record-badge record-badge--missing">${copy.imageMissingShort}</span>` : "";
  return `<button class="clause-list-item ${record.id === selectedId ? "active" : ""} ${record.structuralOnly ? "structural" : ""}" type="button" data-record-id="${escapeHtml(record.id)}"${record.id === selectedId ? ' aria-current="true"' : ""}>
    <strong>${escapeHtml(record.number)}</strong>
    ${assetKind(record.kind) && !title ? "" : `<span>${escapeHtml(title)}</span>`}
    ${badge}${missing}
  </button>`;
}

/**
 * Rebuilds the item lists only. Called on filter and chip changes, never on
 * selection — and it restores scroll so a filter keystroke does not throw the
 * reader back to the top of the Standard.
 */
function renderMenu(): void {
  if (!view) return;
  const container = view.root.querySelector<HTMLElement>(".clause-list-items");
  const scroller = container;
  if (!container || !scroller) return;
  const scrollTop = view.menuRendered ? scroller.scrollTop : view.listScrollTop;
  const copy = appContent.library;
  const records = visibleRecords();
  const byGroup = new Map<string, NavRecord[]>();
  for (const record of records) {
    const key = record.group || "";
    byGroup.set(key, [...(byGroup.get(key) || []), record]);
  }

  const groups = view.groups.filter((group) => (byGroup.get(group.key) || []).length);
  const openGroups = displayedGroups(records);
  container.innerHTML = groups.length ? groups.map((group) => {
    const groupRecords = byGroup.get(group.key) || [];
    // An appendix root is the group heading, so it must not also be listed
    // beneath itself — its children sit under it (design §7). The automatic
    // expansion count uses this same item list, not the selectable heading.
    const items = groupRecords.filter((record) => record.id !== group.rootRecordId);
    const open = openGroups.has(group.key);
    const label = group.kind === "appendix" ? `${copy.appendixPrefix} ${group.key}` : `${copy.sectionPrefix} ${group.key}`;
    // A section is a label only; an appendix root is a real record, so its
    // title doubles as a selectable item (design §7).
    // Only selectable while the root itself survives the filter — otherwise
    // the menu would offer a record the arrows skip, and menu order and arrow
    // order must always agree (design §3.2).
    const rootVisible = Boolean(group.rootRecordId && groupRecords.some((record) => record.id === group.rootRecordId));
    const heading = rootVisible
      ? `<button class="library-group-label ${group.rootRecordId === view!.selectedId ? "active" : ""}" type="button" data-record-id="${escapeHtml(group.rootRecordId!)}">${escapeHtml(label)}${group.title ? ` · ${escapeHtml(group.title)}` : ""}${group.deleted ? ` <span class="record-badge record-badge--deleted">${copy.deletedLabel}</span>` : ""}</button>`
      : `<span class="library-group-label">${escapeHtml(label)}${group.title ? ` · ${escapeHtml(group.title)}` : ""}${group.deleted ? ` <span class="record-badge record-badge--deleted">${copy.deletedLabel}</span>` : ""}</span>`;
    return `<section class="library-group">
      <div class="library-group-header">
        <button class="tree-toggle" type="button" data-group="${escapeHtml(group.key)}" aria-expanded="${open}" aria-label="${escapeHtml(label)}">${open ? "⌄" : "›"}</button>
        ${heading}
        <span class="library-group-count">${groupRecords.length}</span>
      </div>
      ${open ? `<div class="library-group-items">${items.map((record) => itemMarkup(record, view!.selectedId)).join("")}</div>` : ""}
    </section>`;
  // A rejected filter must say why — "nothing matched" would be a lie about a
  // query the server refused to run.
  }).join("") : `<p class="library-empty">${escapeHtml(view.filterError || copy.filterEmpty)}</p>`;

  const count = view.root.querySelector<HTMLElement>("#libraryCount");
  if (count) count.textContent = `${records.length} ${records.length === 1 ? copy.recordSingular : copy.recordPlural}`;

  container.querySelectorAll<HTMLButtonElement>("[data-record-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const record = view!.byId.get(button.dataset.recordId || "");
      if (record) void select(selectionFor(record), true);
    });
  });
  container.querySelectorAll<HTMLButtonElement>("[data-group]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.group!;
      const open = button.getAttribute("aria-expanded") === "true";
      if (open) view!.expanded.delete(key); else view!.expanded.add(key);
      view!.manuallyToggled.add(key);
      renderMenu();
    });
  });
  scroller.scrollTop = scrollTop;
  view.listScrollTop = scrollTop;
  view.menuRendered = true;
}

/** Moves the highlight without touching the rest of the menu. */
function highlightSelection(): void {
  if (!view) return;
  view.root.querySelectorAll<HTMLElement>(".clause-list-item, .library-group-label").forEach((element) => {
    const active = element.getAttribute("data-record-id") === view!.selectedId;
    element.classList.toggle("active", active);
    if (active) element.setAttribute("aria-current", "true"); else element.removeAttribute("aria-current");
  });
}

// ------------------------------------------------------------------- detail

function metaLine(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).map((part) => escapeHtml(part as string)).join(" · ");
}

function flagsMarkup(flags: { normativeStatus: string | null; jurisdictions: string[]; amendments: string[]; requiresReview?: boolean }): string {
  const copy = appContent.library;
  const badges: string[] = [];
  if (flags.normativeStatus === "deleted") badges.push(`<span class="clause-flag clause-flag--deleted">${copy.deletedLabel}</span>`);
  else if (flags.normativeStatus === "informative") badges.push(`<span class="clause-flag">${copy.informativeLabel}</span>`);
  for (const jurisdiction of flags.jurisdictions) {
    if (jurisdiction === "unspecified") continue;
    badges.push(`<span class="clause-flag">${escapeHtml(copy.jurisdictionLabels[jurisdiction] || jurisdiction)}</span>`);
  }
  for (const amendment of flags.amendments) badges.push(`<span class="clause-flag">${copy.amendmentPrefix} ${escapeHtml(amendment)}</span>`);
  if (flags.requiresReview) badges.push(`<span class="clause-flag clause-flag--review">${copy.reviewFlag}</span>`);
  return badges.length ? `<div class="clause-flags">${badges.join("")}</div>` : "";
}

// A figure or table is one logical asset with its segments stacked in part
// order. Segments arrive only for licensed readers, so an asset with none is
// either unlicensed or unverified — both get the same pointer to their own
// copy. The PNGs are stored already upright, so nothing is re-rotated.
function assetImages(asset: { isAvailable: boolean; segments?: Array<{ part: number; url: string }>; altText: string | null; number?: string | null }, label: string): string {
  const copy = appContent.library;
  const segments = asset.isAvailable ? [...(asset.segments || [])].sort((left, right) => left.part - right.part) : [];
  if (!segments.length) return `<p class="clause-asset-missing">${copy.figureFallback}</p>`;
  const segmentLabel = (part: number) => `${asset.altText || label}${segments.length > 1 ? ` (${copy.assetPartPrefix} ${part})` : ""}`;
  const images = segments.map((segment) => `<img class="clause-asset-image" src="${escapeHtml(segment.url)}" alt="${escapeHtml(segmentLabel(segment.part))}" loading="lazy" draggable="false" />`).join("");
  const openLabel = `${copy.openImage}: ${segments.map((segment) => segmentLabel(segment.part)).join(", ")}`;
  return `<div class="clause-asset-images" data-asset-images data-asset-label="${escapeHtml(label)}">
    <div class="asset-viewer-toolbar" role="group" aria-label="${copy.assetZoomControls}">
      <button type="button" data-asset-zoom-out aria-label="${copy.zoomOut}" title="${copy.zoomOut}">−</button>
      <output class="asset-zoom-level" data-asset-zoom-level aria-label="${copy.currentZoom}" aria-live="polite">100%</output>
      <button type="button" data-asset-zoom-in aria-label="${copy.zoomIn}" title="${copy.zoomIn}">+</button>
      <button type="button" data-asset-fit aria-label="${copy.fitImage}" title="${copy.fitImage}">${copy.fitImage}</button>
    </div>
    <div class="asset-viewer-viewport" data-asset-viewport>
      <button class="asset-viewer-stage" type="button" data-open-asset aria-label="${escapeHtml(openLabel)}">${images}</button>
    </div>
  </div>`;
}

// Verified available is a point-in-time claim: the object can be removed after
// verification, or the gated endpoint can fail. A broken image icon is not an
// acceptable substitute for the required wording, and for a multi-part asset
// one bad segment makes the whole thing untrustworthy — so the logical asset
// is replaced as a unit, not segment by segment.
function clearAssetImageBindings(root?: HTMLElement): void {
  const roots = root ? [root] : [...assetImageCleanups.keys()];
  for (const boundRoot of roots) {
    for (const cleanup of assetImageCleanups.get(boundRoot) || []) cleanup();
    assetImageCleanups.delete(boundRoot);
  }
  if (!root) activeLightbox?.close();
}

/** Releases viewers and body-level UI before the application shell is replaced. */
export function teardownLibraryScreen(): void {
  if (view) {
    const scroller = view.root.querySelector<HTMLElement>(".clause-list-items");
    menuSessionState = {
      expanded: new Set(view.expanded),
      manuallyToggled: new Set(view.manuallyToggled),
      chip: view.chip,
      query: view.query,
      // A failed filter left an empty match set behind. Remembering it would
      // show "nothing matched" on return instead of the failure, and the mount
      // would not retry — so a failure is remembered as no result at all.
      matchedIds: view.filterError !== null || !view.matchedIds ? null : new Set(view.matchedIds),
      selectedId: view.selectedId,
      listScrollTop: scroller?.scrollTop ?? view.listScrollTop
    };
  }
  detailVersion += 1;
  subviewVersion += 1;
  filterVersion += 1;
  window.clearTimeout(filterTimer);
  filterTimer = undefined;
  libraryKeydownController?.abort();
  libraryKeydownController = null;
  clearAssetImageBindings();
  subviewHistory = [];
  subviewIndex = -1;
  subviewReturnFocus = null;
  view = null;
}

function bindZoomControls(container: HTMLElement, viewport: HTMLElement, stage: HTMLElement, images: HTMLImageElement[], availableHeight: () => number, sizeViewportToFit = true): () => void {
  const zoomOut = container.querySelector<HTMLButtonElement>("[data-asset-zoom-out]");
  const zoomIn = container.querySelector<HTMLButtonElement>("[data-asset-zoom-in]");
  const fit = container.querySelector<HTMLButtonElement>("[data-asset-fit]");
  const level = container.querySelector<HTMLOutputElement>("[data-asset-zoom-level]");
  if (!zoomOut || !zoomIn || !fit || !level) return () => undefined;

  let scale = 1;
  let fitScale = 1;
  let fitted = true;
  let lastWidth = 0;

  const applyScale = (nextScale: number, resetScroll = false) => {
    const oldWidth = Math.max(1, viewport.scrollWidth);
    const oldHeight = Math.max(1, viewport.scrollHeight);
    const centreX = (viewport.scrollLeft + viewport.clientWidth / 2) / oldWidth;
    const centreY = (viewport.scrollTop + viewport.clientHeight / 2) / oldHeight;
    scale = Math.min(ASSET_MAX_SCALE, Math.max(fitScale, nextScale));
    const naturalWidth = Math.max(...images.map((image) => image.naturalWidth));
    stage.style.width = `${naturalWidth * scale}px`;
    images.forEach((image) => { image.style.width = `${image.naturalWidth * scale}px`; });
    level.value = `${Math.round(scale * 100)}%`;
    level.textContent = level.value;
    fitted = Math.abs(scale - fitScale) < 0.001;
    zoomOut.disabled = fitted;
    zoomIn.disabled = scale >= ASSET_MAX_SCALE - 0.001;
    fit.disabled = fitted;
    if (resetScroll) {
      viewport.scrollTo(0, 0);
    } else {
      viewport.scrollLeft = centreX * viewport.scrollWidth - viewport.clientWidth / 2;
      viewport.scrollTop = centreY * viewport.scrollHeight - viewport.clientHeight / 2;
    }
  };

  const recalculateFit = () => {
    const width = viewport.clientWidth;
    if (!width) return;
    const wasFitted = fitted;
    fitScale = fitScaleFor(images.map(naturalSize), width, availableHeight());
    const fittedHeight = fittedStackHeight(images.map(naturalSize), fitScale);
    if (sizeViewportToFit) viewport.style.height = `${Math.ceil(fittedHeight)}px`;
    applyScale(wasFitted ? fitScale : scale, wasFitted);
    lastWidth = width;
  };

  zoomOut.addEventListener("click", () => applyScale(scale / ASSET_ZOOM_STEP));
  zoomIn.addEventListener("click", () => applyScale(scale * ASSET_ZOOM_STEP));
  fit.addEventListener("click", () => applyScale(fitScale, true));
  recalculateFit();

  const resizeObserver = new ResizeObserver(() => {
    if (Math.abs(viewport.clientWidth - lastWidth) > 1) recalculateFit();
  });
  resizeObserver.observe(container);
  window.addEventListener("resize", recalculateFit);
  return () => {
    resizeObserver.disconnect();
    window.removeEventListener("resize", recalculateFit);
  };
}

function openAssetLightbox(source: HTMLElement, images: HTMLImageElement[], fallBack: () => void): void {
  activeLightbox?.close();
  const copy = appContent.library;
  const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const overlay = document.createElement("div");
  overlay.className = "asset-lightbox";
  overlay.innerHTML = `
    <div class="asset-lightbox-dialog" role="dialog" aria-modal="true" aria-label="${copy.enlargedImage}: ${escapeHtml(source.dataset.assetLabel || "")}">
      <div class="asset-viewer-toolbar asset-lightbox-toolbar" role="group" aria-label="${copy.assetZoomControls}">
        <button type="button" data-asset-zoom-out aria-label="${copy.zoomOut}" title="${copy.zoomOut}">−</button>
        <output class="asset-zoom-level" data-asset-zoom-level aria-label="${copy.currentZoom}" aria-live="polite">100%</output>
        <button type="button" data-asset-zoom-in aria-label="${copy.zoomIn}" title="${copy.zoomIn}">+</button>
        <button type="button" data-asset-fit aria-label="${copy.fitImage}" title="${copy.fitImage}">${copy.fitImage}</button>
        <button class="asset-lightbox-close" type="button" data-lightbox-close aria-label="${copy.closeEnlargedImage}">${copy.closeEnlargedImage}</button>
      </div>
      <div class="asset-lightbox-viewport" data-asset-viewport tabindex="0" aria-label="${copy.panImage}">
        <div class="asset-lightbox-stage"></div>
      </div>
    </div>`;
  const viewport = overlay.querySelector<HTMLElement>("[data-asset-viewport]")!;
  const stage = overlay.querySelector<HTMLElement>(".asset-lightbox-stage")!;
  const clones = images.map((image) => {
    const clone = image.cloneNode(false) as HTMLImageElement;
    clone.removeAttribute("loading");
    clone.style.width = "";
    stage.appendChild(clone);
    return clone;
  });
  const controller = new AbortController();
  const oldBodyOverflow = document.body.style.overflow;
  let zoomCleanup: () => void = () => undefined;
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    controller.abort();
    zoomCleanup();
    overlay.remove();
    document.body.style.overflow = oldBodyOverflow;
    if (activeLightbox?.source === source) activeLightbox = null;
    if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
  };
  activeLightbox = { close, source };
  document.body.appendChild(overlay);
  document.body.style.overflow = "hidden";
  const closeButton = overlay.querySelector<HTMLButtonElement>("[data-lightbox-close]");
  closeButton?.focus();

  const initialize = () => {
    if (closed || clones.some((image) => !image.complete)) return;
    if (clones.some((image) => image.naturalWidth === 0)) { close(); fallBack(); return; }
    zoomCleanup();
    zoomCleanup = bindZoomControls(overlay, viewport, stage, clones, () => viewport.clientHeight, false);
  };
  clones.forEach((image) => {
    image.addEventListener("load", initialize, { signal: controller.signal });
    image.addEventListener("error", () => { close(); fallBack(); }, { signal: controller.signal });
  });
  initialize();

  closeButton?.addEventListener("click", close, { signal: controller.signal });
  overlay.addEventListener("click", (event) => { if (event.target === overlay) close(); }, { signal: controller.signal });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") { event.preventDefault(); close(); }
  }, { signal: controller.signal });
  overlay.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...overlay.querySelectorAll<HTMLElement>("button:not(:disabled), [tabindex='0']")];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }, { signal: controller.signal });

  let dragStart: { x: number; y: number; left: number; top: number } | null = null;
  viewport.addEventListener("pointerdown", (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    dragStart = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.classList.add("dragging");
    viewport.setPointerCapture(event.pointerId);
  }, { signal: controller.signal });
  viewport.addEventListener("pointermove", (event) => {
    if (!dragStart) return;
    viewport.scrollLeft = dragStart.left - (event.clientX - dragStart.x);
    viewport.scrollTop = dragStart.top - (event.clientY - dragStart.y);
  }, { signal: controller.signal });
  const endDrag = () => { dragStart = null; viewport.classList.remove("dragging"); };
  viewport.addEventListener("pointerup", endDrag, { signal: controller.signal });
  viewport.addEventListener("pointercancel", endDrag, { signal: controller.signal });
}

function bindAssetImages(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>("[data-asset-images]").forEach((group) => {
    const controller = new AbortController();
    let disposed = false;
    let failed = false;
    let initialized = false;
    let zoomCleanup: () => void = () => undefined;
    const fallBack = () => {
      if (disposed || failed) return;
      failed = true;
      controller.abort();
      zoomCleanup();
      if (activeLightbox?.source === group) activeLightbox.close();
      group.innerHTML = `<p class="clause-asset-missing">${appContent.library.figureFallback}</p>`;
    };
    const images = [...group.querySelectorAll<HTMLImageElement>("img")];
    const initialize = () => {
      if (disposed || failed || initialized || images.some((image) => !image.complete)) return;
      if (images.some((image) => image.naturalWidth === 0)) { fallBack(); return; }
      const viewport = group.querySelector<HTMLElement>("[data-asset-viewport]");
      const stage = group.querySelector<HTMLElement>("[data-open-asset]");
      if (!viewport || !stage) return;
      initialized = true;
      zoomCleanup = bindZoomControls(group, viewport, stage, images, () => Math.max(120, Math.min(window.innerHeight * 0.65, 720)));
      stage.addEventListener("click", () => openAssetLightbox(group, images, fallBack), { signal: controller.signal });
    };
    images.forEach((image) => {
      image.addEventListener("load", initialize, { signal: controller.signal });
      image.addEventListener("error", fallBack, { signal: controller.signal });
    });
    // An image can already have failed before this runs.
    if (images.some((image) => image.complete && image.naturalWidth === 0)) fallBack();
    initialize();
    const cleanups = assetImageCleanups.get(root) || [];
    cleanups.push(() => {
      if (disposed) return;
      disposed = true;
      controller.abort();
      zoomCleanup();
      if (activeLightbox?.source === group) activeLightbox.close();
    });
    assetImageCleanups.set(root, cleanups);
  });
}

type ReferenceCandidate = { text: string; selection: LibrarySelection };

function referenceCandidates(clause: ClauseDetail): ReferenceCandidate[] {
  const candidates: ReferenceCandidate[] = [
    ...clause.related
      .filter((item) => item.source === "reference")
      .map((item) => ({ text: item.clauseNumber, selection: { kind: "clause" as const, id: item.id } })),
    ...clause.assets
      .filter((asset) => Boolean(asset.displayedIdentifier))
      .map((asset) => ({ text: asset.displayedIdentifier!, selection: { kind: "asset" as const, id: asset.id } }))
  ];
  const unique = new Map<string, ReferenceCandidate>();
  for (const candidate of candidates) {
    if (candidate.text && !unique.has(candidate.text)) unique.set(candidate.text, candidate);
  }
  return [...unique.values()].sort((left, right) => right.text.length - left.text.length);
}

function referenceBoundaryIsValid(text: string, start: number, length: number): boolean {
  const before = text[start - 1] || "";
  const first = text[start] || "";
  const end = start + length;
  const after = text[end] || "";
  const afterNext = text[end + 1] || "";
  const invalidBefore = /\d/.test(before) || (before === "." && /\d/.test(first));
  const invalidAfter = /\d/.test(after) || (after === "." && /\d/.test(afterNext));
  return !invalidBefore && !invalidAfter;
}

/**
 * Linkifies only extracted targets, after the server-sanitized body has been
 * inserted. One alternation scans each text node once; longest-first ordering
 * prevents a shorter clause number from consuming a longer candidate.
 */
function linkifyClauseBody(root: HTMLElement, clause: ClauseDetail): void {
  if (!clause.bodyHtml) return;
  const candidates = referenceCandidates(clause);
  if (!candidates.length) return;
  const byText = new Map(candidates.map((candidate) => [candidate.text, candidate]));
  const pattern = candidates.map((candidate) => candidate.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const matcher = new RegExp(pattern, "g");
  const bodies = root.querySelectorAll<HTMLElement>(".clause-body-html");

  bodies.forEach((body) => {
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.parentElement?.closest("a") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      }
    });
    const textNodes: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) textNodes.push(node as Text);

    for (const textNode of textNodes) {
      const text = textNode.data;
      matcher.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      let rewritten = false;
      for (let match = matcher.exec(text); match; match = matcher.exec(text)) {
        const candidate = byText.get(match[0]);
        // A rejected match must not consume its whole span: a shorter candidate
        // can start inside it and still be validly bounded. Resuming one
        // character in keeps that reachable, and always advances, so the scan
        // cannot stall.
        if (!candidate || !referenceBoundaryIsValid(text, match.index, match[0].length)) {
          matcher.lastIndex = match.index + 1;
          continue;
        }
        fragment.append(text.slice(cursor, match.index));
        const link = document.createElement("a");
        link.className = "clause-reference-link";
        link.href = candidate.selection.kind === "clause"
          ? `#/clause/${encodeURIComponent(candidate.selection.id)}`
          : `#/asset/${encodeURIComponent(candidate.selection.id)}`;
        link.textContent = match[0];
        link.dataset.referenceLink = "true";
        if (candidate.selection.kind === "clause") link.dataset.clauseId = candidate.selection.id;
        else {
          link.dataset.recordId = candidate.selection.id;
          link.dataset.recordKind = "asset";
        }
        fragment.append(link);
        cursor = match.index + match[0].length;
        rewritten = true;
      }
      if (!rewritten) continue;
      fragment.append(text.slice(cursor));
      textNode.replaceWith(fragment);
    }
  });
}

function clauseAssetMarkup(asset: ClauseAsset): string {
  const copy = appContent.library;
  const label = asset.displayedIdentifier || asset.assetType;
  const caption = captionWithoutIdentifier(asset.displayedIdentifier, asset.caption);
  return `<article class="clause-asset">
    <strong>${escapeHtml(label)}</strong>
    ${caption ? `<p>${escapeHtml(caption)}</p>` : ""}
    ${asset.firstPrintedPage ? `<p class="clause-asset-page">${copy.pagePrefix} ${asset.firstPrintedPage}</p>` : ""}
    ${assetImages(asset, label)}
  </article>`;
}

function clauseBodyMarkup(clause: ClauseDetail): string {
  const copy = appContent.library;
  if (clause.bodyHtml) return `<section class="library-verbatim"><strong>${copy.standardLabel}</strong><div class="clause-body-html">${clause.bodyHtml}</div></section>`;
  if (clause.verbatimText) return `<section class="library-verbatim"><strong>${copy.standardLabel}</strong><p>${escapeHtml(clause.verbatimText)}</p></section>`;
  // A heading-only parent has no body of its own — that is the record, not a
  // gap, so it must not read as licensed content being withheld.
  if (!clause.hasDirectContent) return `<p class="library-pointer">${copy.headingOnly}</p>`;
  return `<p class="library-pointer">${copy.licensedPointer}</p>`;
}

function linkListMarkup(heading: string, items: Array<{ id: string; clauseNumber: string; title: string; badge?: string }>): string {
  if (!items.length) return "";
  return `<section><h3>${heading}</h3><div class="clause-link-list">${items.map((item) => `<button type="button" data-clause-id="${escapeHtml(item.id)}"><strong>${escapeHtml(item.clauseNumber)}</strong> ${escapeHtml(item.title)}${item.badge ? ` <span class="match-badge">${escapeHtml(item.badge)}</span>` : ""}</button>`).join("")}</div></section>`;
}

/** Previous/next step through the currently filtered list (design §3.3). */
function arrowsMarkup(): string {
  const copy = appContent.library;
  const records = visibleRecords();
  const index = records.findIndex((record) => record.id === view?.selectedId);
  const prev = index > 0 ? records[index - 1] : null;
  const next = index >= 0 ? records[index + 1] : null;
  const button = (record: NavRecord | null, label: string) =>
    `<button class="secondary" type="button" ${record ? `data-record-id="${escapeHtml(record.id)}"` : "disabled"} title="${record ? escapeHtml(record.number) : ""}">${label}</button>`;
  return `<div class="clause-pagination">${button(prev, copy.previous)}${button(next, copy.next)}</div>`;
}

function reportIssueMarkup(): string {
  const copy = appContent.library;
  return `<button class="secondary report-issue-button" type="button">${copy.reportIssue}</button>
    <dialog class="report-issue-dialog" aria-label="${copy.reportDialogHeading}">
      <form class="report-issue-form">
        <h3>${copy.reportDialogHeading}</h3>
        <label>${copy.reportIssueType}
          <select name="issueType" required>${Object.entries(copy.issueTypeLabels).map(([key, label]) => `<option value="${escapeHtml(key)}">${escapeHtml(label)}</option>`).join("")}</select>
        </label>
        <label>${copy.reportNote}
          <textarea name="note" maxlength="1000" placeholder="${copy.reportNotePlaceholder}"></textarea>
        </label>
        <p class="report-issue-status" role="status"></p>
        <div class="row-actions">
          <button type="submit">${copy.reportSubmit}</button>
          <button class="ghost report-issue-cancel" type="button">${copy.reportCancel}</button>
        </div>
      </form>
    </dialog>`;
}

function clauseDetailMarkup(clause: ClauseDetail, target: DetailTarget = "main"): string {
  const copy = appContent.library;
  const where = clause.appendix ? `${copy.appendixPrefix} ${clause.appendix}`
    : /^\d+$/.test(clause.section) ? `${copy.sectionPrefix} ${clause.section}` : clause.section;
  const page = clause.pageReference || (clause.firstPrintedPage ? `${copy.pagePrefix} ${clause.firstPrintedPage}` : "");
  return `
    ${clause.breadcrumbs.length ? `<nav class="clause-breadcrumbs" aria-label="${copy.breadcrumbs}">${clause.breadcrumbs.map((item) => `<button type="button" data-clause-id="${escapeHtml(item.id)}">${escapeHtml(item.clauseNumber)}</button>`).join("<span>›</span>")}</nav>` : ""}
    <span class="eyebrow">${copy.selectedLabel}</span>
    <h2 class="clause-heading"><span class="clause-heading-number">${escapeHtml(clause.clauseNumber)}</span> <span class="clause-heading-title">${escapeHtml(clause.title)}</span></h2>
    <p class="clause-heading-meta">${metaLine([where, clause.sectionTitle, page])}</p>
    ${flagsMarkup(clause)}
    ${clause.jimRead ? `<section class="library-read"><strong>${copy.jimReadLabel}</strong><p>${escapeHtml(clause.jimRead)}</p></section>` : ""}
    ${target === "main" ? arrowsMarkup() : ""}
    ${clauseBodyMarkup(clause)}
    ${linkListMarkup(copy.children, clause.children)}
    ${linkListMarkup(copy.related, clause.related.map((item) => ({ ...item, badge: item.source === "cited-by" ? copy.citedBy : item.source === "curated" ? copy.curated : copy.reference })))}
    ${clause.unresolvedReferences.length ? `<section><h3>${copy.unresolvedReferences}</h3><div class="unresolved-reference-list">${clause.unresolvedReferences.map((item) => `<p><span class="match-badge">${escapeHtml((copy.referenceTypeLabels as Record<string, string>)[item.type] || item.type)}</span> ${escapeHtml(item.targetRef)}</p>`).join("")}</div></section>` : ""}
    ${clause.assets.length ? `<section><h3>${copy.assets}</h3>${clause.assets.map(clauseAssetMarkup).join("")}</section>` : ""}
    ${target === "main" ? `<div class="clause-actions">
      <button id="askSelectedClause" type="button">${copy.askJim}</button>
      <button id="saveSelectedClause" class="secondary" type="button">${copy.saveNote}</button>
      <button class="secondary clause-copy" data-format="text" type="button">${copy.copyText}</button>
      <button class="secondary clause-copy" data-format="markdown" type="button">${copy.copyMarkdown}</button>
      <button class="secondary clause-screen-grab" type="button">${copy.screenGrab}</button>
      ${reportIssueMarkup()}
    </div>` : ""}
  `;
}

function assetDetailMarkup(asset: AssetDetail, target: DetailTarget = "main"): string {
  const copy = appContent.library;
  const where = /^[A-Z]$/i.test(asset.group || "") ? `${copy.appendixPrefix} ${asset.group}` : `${copy.sectionPrefix} ${asset.group}`;
  const caption = captionWithoutIdentifier(asset.number, asset.caption);
  return `
    <nav class="clause-breadcrumbs" aria-label="${copy.breadcrumbs}"><button type="button" data-clause-id="${escapeHtml(asset.owningClause.id)}">${escapeHtml(asset.owningClause.clauseNumber)}</button></nav>
    <span class="eyebrow">${copy.kindLabels[asset.kind] || copy.selectedLabel}</span>
    <h2 class="clause-heading"><span class="clause-heading-number">${escapeHtml(asset.number)}</span>${caption ? ` <span class="clause-heading-title">${escapeHtml(caption)}</span>` : ""}</h2>
    <p class="clause-heading-meta">${metaLine([where, asset.sectionTitle, asset.pageReference])}</p>
    ${flagsMarkup(asset)}
    ${target === "main" ? arrowsMarkup() : ""}
    <section class="library-verbatim"><strong>${copy.standardLabel}</strong>
      <article class="clause-asset">${assetImages(asset, asset.number)}</article>
    </section>
    ${linkListMarkup(copy.ownedBy, [asset.owningClause])}
    ${linkListMarkup(copy.referencedBy, asset.referencedBy)}
    ${target === "main" && (asset.kind === "figure" || asset.kind === "table") ? `<div class="clause-actions">${reportIssueMarkup()}</div>` : ""}
  `;
}

// The renderer is the app's only runtime dependency and dwarfs the rest of the
// bundle, so it is fetched on the first grab rather than on every page load.
let screenGrabRenderer: Promise<typeof import("html2canvas-pro")> | null = null;
function loadScreenGrabRenderer(): Promise<typeof import("html2canvas-pro")> {
  // A rejected promise must not stay in the cache. Caching one - after a dropped
  // connection, say - would make every later grab fail instantly with the first
  // failure, and pressing the button again would never re-attempt the download.
  return (screenGrabRenderer ||= import("html2canvas-pro").catch((error) => {
    screenGrabRenderer = null;
    throw error;
  }));
}

function wrapStamp(context: CanvasRenderingContext2D, stamp: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of stamp.split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean).flatMap((word) => {
      if (context.measureText(word).width <= maxWidth) return [word];
      const pieces: string[] = [];
      let piece = "";
      for (const character of word) {
        if (piece && context.measureText(piece + character).width > maxWidth) { pieces.push(piece); piece = character; }
        else piece += character;
      }
      if (piece) pieces.push(piece);
      return pieces;
    });
    if (!words.length) { lines.push(""); continue; }
    let line = words.shift()!;
    for (const word of words) {
      const candidate = `${line} ${word}`;
      if (context.measureText(candidate).width <= maxWidth) line = candidate;
      else { lines.push(line); line = word; }
    }
    lines.push(line);
  }
  return lines;
}

/**
 * The bytes we validate must be the bytes that get rendered. Fetching only to
 * check availability would let html2canvas-pro reload each image itself, and a
 * failure on that second request produces a blank box instead of the required
 * missing-image notice. Inlining as object URLs also means the session-gated
 * images are fetched exactly once.
 */
type CapturedPane = { canvas: HTMLCanvasElement; width: number; height: number };

/**
 * Reads an image's intrinsic size out of its bytes, for the case where the
 * element cannot supply one — a lazy image below the fold has not decoded, so
 * it reports zero until something makes it load. Returns null rather than
 * throwing: a size we cannot establish is handled as a missing image.
 */
async function intrinsicSize(blob: Blob): Promise<ImageSize | null> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(blob);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      return size;
    } catch {
      // Falls through. A rejection here does not mean the browser cannot read
      // the image — `createImageBitmap` refuses formats the <img> decoder
      // accepts — and stopping would leave the clone free to decode a size
      // measurement never saw.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image.naturalWidth > 0 && image.naturalHeight > 0
      ? { width: image.naturalWidth, height: image.naturalHeight }
      : null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Replays onto html2canvas's clone exactly what measurement recorded, so the
 * rendered pane and the frame measured for it cannot drift apart. Everything it
 * needs travels as data attributes, which survive cloning; nothing here measures.
 */
function applyCapturePlan(clone: HTMLElement): void {
  clone.style.maxHeight = "none";
  clone.style.overflow = "visible";
  clone.querySelectorAll<HTMLElement>(captureHiddenSelectors().join(",")).forEach((element) => {
    element.style.display = "none";
  });
  clone.querySelectorAll<HTMLElement>("[data-grab-fallback]").forEach((group) => {
    const fallback = Object.assign(clone.ownerDocument.createElement("p"), {
      className: "clause-asset-missing", textContent: appContent.library.figureFallback
    });
    group.replaceWith(fallback);
  });
  clone.querySelectorAll<HTMLElement>("[data-grab-height]").forEach((element) => {
    element.style.height = element.dataset.grabHeight || "";
  });
  clone.querySelectorAll<HTMLElement>("[data-grab-width]").forEach((element) => {
    element.style.width = element.dataset.grabWidth || "";
  });
}

/** Strips the recorded plan from the live pane once the capture is done with it. */
function clearCapturePlan(pane: HTMLElement): void {
  pane.querySelectorAll<HTMLElement>("[data-grab-height], [data-grab-width], [data-grab-fallback]").forEach((element) => {
    delete element.dataset.grabHeight;
    delete element.dataset.grabWidth;
    delete element.dataset.grabFallback;
  });
}

function preparePaneForCapture(pane: HTMLElement, failedSources: ReadonlySet<string>, sizeOf: (image: HTMLImageElement) => ImageSize): () => void {
  const originalStyles = new Map<HTMLElement, string | null>();
  const originalScroll = new Map<HTMLElement, { top: number; left: number }>();
  const remember = (element: HTMLElement) => {
    if (!originalStyles.has(element)) originalStyles.set(element, element.getAttribute("style"));
  };
  // Hiding a row and resizing a viewport both reflow the pane, which silently
  // clamps any scroll position inside it. Restoring styles alone would leave the
  // reader looking somewhere else than before they pressed the button.
  const rememberScroll = (element: HTMLElement) => {
    if (!originalScroll.has(element)) originalScroll.set(element, { top: element.scrollTop, left: element.scrollLeft });
  };
  const undoReplacements: Array<() => void> = [];
  /** The same swap `onclone` performs, so measurement sees the geometry the clone renders. */
  const replaceWithFallback = (group: HTMLElement) => {
    const document = group.ownerDocument;
    const fallback = Object.assign(document.createElement("p"), {
      className: "clause-asset-missing", textContent: appContent.library.figureFallback
    });
    group.replaceWith(fallback);
    // The group element itself is kept, so anything bound to it survives.
    undoReplacements.push(() => fallback.replaceWith(group));
  };
  const restore = () => {
    undoReplacements.forEach((undo) => undo());
    originalStyles.forEach((style, element) => {
      if (style === null) element.removeAttribute("style");
      else element.setAttribute("style", style);
    });
    originalScroll.forEach((position, element) => {
      element.scrollTop = position.top;
      element.scrollLeft = position.left;
    });
  };

  try {
    rememberScroll(pane);
    pane.querySelectorAll<HTMLElement>("[data-asset-viewport]").forEach(rememberScroll);
    // The pane itself is a scroll box — `.clause-detail` is capped to the window
    // and `.reference-subview` to 68vh — so its clientHeight is the size of the
    // window onto the record, not the record. Expanding an asset inside it
    // without lifting that cap would crop the asset rather than show it whole,
    // which is worse than the shrinking this change exists to fix.
    remember(pane);
    pane.style.maxHeight = "none";
    pane.style.overflow = "visible";
    pane.querySelectorAll<HTMLElement>(captureHiddenSelectors().join(",")).forEach((element) => {
      remember(element);
      element.style.display = "none";
    });
    pane.querySelectorAll<HTMLElement>("[data-asset-images]").forEach((group) => {
      const viewport = group.querySelector<HTMLElement>("[data-asset-viewport]");
      const stage = group.querySelector<HTMLElement>("[data-open-asset]");
      const images = [...group.querySelectorAll<HTMLImageElement>("img")];
      if (!viewport || !stage || !images.length) return;
      // Checked before the natural-size guard, not after. A lazy image that
      // never loaded has no natural size *and* fails the re-fetch, and the guard
      // would return first — leaving the group measured at its current height
      // while `onclone` still swapped in the notice, which is the drift this is
      // here to prevent.
      //
      // A group whose bytes failed is replaced wholesale by the missing-image
      // notice in the clone. Measurement has to do the same, or the captured
      // height keeps the space of an asset that is never drawn and the image
      // gets a tall blank band with the stamp pushed below it.
      if (images.some((image) => failedSources.has(image.src))) {
        group.dataset.grabFallback = "";
        replaceWithFallback(group);
        return;
      }
      // Sizes come from the resolver, not straight from the element. A lazy
      // image that has not decoded reports zero even when its bytes fetched
      // perfectly well — and `onclone` will hand it the fetched object URL, so
      // it would decode and expand past the frame measured around its zero.
      const sizes = images.map(sizeOf);
      // A size neither the element nor the bytes could supply is treated exactly
      // like a failed fetch. Returning here instead would leave the group
      // measured at its current height while the clone still held the fetched
      // object URL — free to decode and expand past that frame — and, if it
      // could not decode either, would leave out the missing-image notice the
      // rendering contract requires.
      if (sizes.some((size) => size.width <= 0 || size.height <= 0)) {
        group.dataset.grabFallback = "";
        replaceWithFallback(group);
        return;
      }
      const scale = captureFitScale(sizes, viewport.clientWidth);
      const fittedHeight = fittedStackHeight(sizes, scale);
      remember(viewport);
      remember(stage);
      images.forEach(remember);
      const viewportHeight = `${Math.ceil(fittedHeight)}px`;
      const stageWidth = `${Math.max(...sizes.map((size) => size.width)) * scale}px`;
      viewport.style.height = viewportHeight;
      stage.style.width = stageWidth;
      // Recorded as data, not just applied as style. `restore()` strips the
      // styles before html2canvas clones the pane, but these survive into the
      // clone, where the sizes cannot be recomputed: a cloned <img> lives in a
      // fresh document and may report a natural size of 0 until it has loaded,
      // which would silently leave the clone unexpanded and render a short
      // asset into the tall frame that was measured for it.
      viewport.dataset.grabHeight = viewportHeight;
      stage.dataset.grabWidth = stageWidth;
      images.forEach((image, index) => {
        const width = `${sizes[index].width * scale}px`;
        const height = `${sizes[index].height * scale}px`;
        image.style.width = width;
        image.style.height = height;
        // Height as well as width. Width alone leaves height at `auto`, which
        // resolves from the intrinsic size — and a cloned image reporting a
        // natural size of zero would then lay out zero pixels tall inside the
        // explicitly tall viewport, drawing a blank where the asset should be.
        image.dataset.grabWidth = width;
        image.dataset.grabHeight = height;
      });
    });
    return restore;
  } catch (error) {
    restore();
    throw error;
  }
}

async function captureVisiblePane(pane: HTMLElement, scale: number, backgroundColor: string): Promise<CapturedPane> {
  // Loaded first, before a single object URL is minted or an attribute set on
  // the live pane. It is a 249 kB dynamic import that only arrives on first
  // use: awaiting it later would either leave that state behind when the
  // download fails, or - if the pane were prepared by then - show the reader an
  // expanded, button-less version of the app for as long as it took.
  const { default: html2canvas } = await loadScreenGrabRenderer();
  const images = [...pane.querySelectorAll<HTMLImageElement>("img")];
  const inlined = new Map<string, string>();
  const failed = new Set<string>();
  const decoded = new Map<string, ImageSize>();
  // The element's own natural size when it has one, and the size read back from
  // the bytes when it does not. `loading="lazy"` images below the fold report
  // zero, and the clone is about to be handed the fetched bytes, so measuring
  // from the element alone would frame the asset around a size it is not.
  const sizeOf = (image: HTMLImageElement): ImageSize =>
    (image.naturalWidth > 0 && image.naturalHeight > 0 ? naturalSize(image) : decoded.get(image.src)) ?? { width: 0, height: 0 };
  const marker = crypto.randomUUID();
  try {
    await Promise.all(images.map(async (image) => {
      const source = image.src;
      if (inlined.has(source) || failed.has(source)) return;
      try {
        const response = await fetch(source, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Image unavailable");
        const blob = await response.blob();
        inlined.set(source, URL.createObjectURL(blob));
        const size = await intrinsicSize(blob);
        if (size) decoded.set(source, size);
      } catch { failed.add(source); }
    }));
    pane.dataset.screenGrabPane = marker;
    // Measured with the preparation applied and then undone in the same
    // synchronous block, so the browser never paints the prepared state:
    // html2canvas needs the dimensions up front, but the reader must not see
    // the pane change. The same preparation is applied again inside `onclone`,
    // to the copy html2canvas actually renders.
    const measureRestore = preparePaneForCapture(pane, failed, sizeOf);
    let width = 0;
    let height = 0;
    try {
      pane.getBoundingClientRect();
      width = pane.clientWidth;
      height = pane.clientHeight;
    } finally {
      // Restored from a finally, not after the reads. If a layout read throws,
      // the outer finally only strips the plan attributes — it would leave the
      // pane expanded, its buttons hidden and its scroll moved.
      measureRestore();
    }
    // Held down per pane, not just on the composite. Capture no longer caps an
    // asset's height, so a tall table can now make a single pane thousands of
    // pixels tall — past what a browser will allocate once the device pixel
    // ratio is applied, which would return a blank canvas.
    const paneScale = cappedCaptureScale(scale, width, height);
    const canvas = await html2canvas(pane, {
      useCORS: true,
      backgroundColor,
      scale: paneScale,
      x: 0,
      y: 0,
      width,
      height,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      logging: false,
      foreignObjectRendering: false,
      onclone: (clonedDocument) => {
        const clone = clonedDocument.querySelector<HTMLElement>(`[data-screen-grab-pane="${marker}"]`);
        if (clone) applyCapturePlan(clone);
        clone?.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
          image.removeAttribute("loading");
          const objectUrl = inlined.get(image.src);
          if (objectUrl) { image.src = objectUrl; return; }
          const group = image.closest("[data-asset-images]");
          const fallback = Object.assign(clonedDocument.createElement("p"), { className: "clause-asset-missing", textContent: appContent.library.figureFallback });
          if (group) group.replaceWith(fallback); else image.replaceWith(fallback);
        });
      }
    });
    return { canvas, width, height };
  } finally {
    clearCapturePlan(pane);
    delete pane.dataset.screenGrabPane;
    inlined.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  }
}

async function renderScreenGrab(main: HTMLElement, subview: HTMLElement | null, stamp: string): Promise<Blob> {
  const paneGap = subview ? 18 : 0;
  const captureScale = Math.max(0.1, Math.min(window.devicePixelRatio || 1, 2));
  const surface = getComputedStyle(document.documentElement).getPropertyValue("--paper-surface").trim() || "#faf7ef";
  const mainCapture = await captureVisiblePane(main, captureScale, surface);
  const subviewCapture = subview ? await captureVisiblePane(subview, captureScale, surface) : null;
  const width = Math.max(mainCapture.width, subviewCapture?.width || 0);
  const measureCanvas = document.createElement("canvas");
  const measureContext = measureCanvas.getContext("2d");
  if (!measureContext) throw new Error("Canvas unavailable");
  const stampFont = '600 13px "IBM Plex Mono", Consolas, monospace';
  measureContext.font = stampFont;
  const stampLines = wrapStamp(measureContext, stamp, Math.max(1, width - 36));
  const stampLineHeight = 20;
  const stampHeight = 16 + stampLines.length * stampLineHeight + 16 + 4;
  const height = mainCapture.height + (subviewCapture?.height || 0) + paneGap + stampHeight;
  const scale = cappedCaptureScale(captureScale, width, height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width * scale); canvas.height = Math.ceil(height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.scale(scale, scale);
  context.fillStyle = surface; context.fillRect(0, 0, width, height);
  context.drawImage(mainCapture.canvas, 0, 0, mainCapture.width, mainCapture.height);
  let y = mainCapture.height;
  if (subviewCapture) {
    y += paneGap;
    context.drawImage(subviewCapture.canvas, 0, y, subviewCapture.width, subviewCapture.height);
    y += subviewCapture.height;
  }
  context.fillStyle = "#b87333"; context.fillRect(0, y, width, 4);
  context.fillStyle = "#2b2924"; context.fillRect(0, y + 4, width, stampHeight - 4);
  context.fillStyle = "#faf7ef"; context.font = stampFont; context.textBaseline = "top";
  stampLines.forEach((line, index) => context.fillText(line, 18, y + 20 + index * stampLineHeight));
  return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("PNG unavailable")), "image/png"));
}

async function writeScreenGrab(blob: Blob): Promise<void> {
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try { await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]); return; } catch { /* Mobile clipboard implementations commonly reject image writes. */ }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = `${appContent.library.screenGrabFilename}.png`;
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function bindDetail(detail: HTMLElement, record: { id: string; kind: RecordKind; number: string; title: string | null }, copyable: boolean, target: DetailTarget): void {
  bindAssetImages(detail);
  detail.querySelectorAll<HTMLElement>("[data-clause-id]").forEach((control) => {
    control.addEventListener("click", (event) => {
      event.preventDefault();
      if (!control.dataset.clauseId) return;
      const selection: LibrarySelection = { kind: "clause", id: control.dataset.clauseId };
      if (target === "subview" || control.dataset.referenceLink) void openSubview(selection, target === "main" ? control : undefined);
      else void select(selection, true);
    });
  });
  detail.querySelectorAll<HTMLElement>("[data-record-id]").forEach((control) => {
    control.addEventListener("click", (event) => {
      event.preventDefault();
      const record = view?.byId.get(control.dataset.recordId || "");
      const selection = record ? selectionFor(record)
        : control.dataset.recordId && control.dataset.recordKind === "asset" ? { kind: "asset" as const, id: control.dataset.recordId }
        : null;
      if (!selection) return;
      if (target === "subview" || control.dataset.referenceLink) void openSubview(selection, target === "main" ? control : undefined);
      else void select(selection, true);
    });
  });

  if (target !== "main") return;
  const ask = () => view?.options.openSearch(`${appContent.library.explainPrefix} ${record.number} ${record.title || ""}`.trim());
  const top = view?.root.querySelector<HTMLButtonElement>("#libraryAskButton");
  if (top) {
    top.disabled = false;
    top.onclick = ask;
  }
  detail.querySelector<HTMLButtonElement>("#askSelectedClause")?.addEventListener("click", ask);
  detail.querySelector<HTMLButtonElement>("#saveSelectedClause")?.addEventListener("click", () => {
    view?.options.saveAnswer(`${appContent.library.noteQuestionPrefix} ${record.number}`, record.title || record.number, [record.number]);
    view?.options.openSavedAnswers();
  });
  const reportButton = detail.querySelector<HTMLButtonElement>(".report-issue-button");
  const reportDialog = detail.querySelector<HTMLDialogElement>(".report-issue-dialog");
  const reportForm = detail.querySelector<HTMLFormElement>(".report-issue-form");
  const reportStatus = detail.querySelector<HTMLElement>(".report-issue-status");
  reportButton?.addEventListener("click", () => {
    if (!reportDialog) return;
    if (reportStatus) reportStatus.textContent = "";
    reportDialog.showModal();
    (reportForm?.querySelector("select") as HTMLSelectElement | null)?.focus();
  });
  detail.querySelector<HTMLButtonElement>(".report-issue-cancel")?.addEventListener("click", () => reportDialog?.close());
  reportForm?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!reportButton || !reportDialog || (record.kind !== "clause" && record.kind !== "figure" && record.kind !== "table")) return;
    const submit = reportForm.querySelector<HTMLButtonElement>("button[type='submit']");
    const form = new FormData(reportForm);
    submit?.setAttribute("disabled", "");
    try {
      await view!.options.reportRecord({
        record_id: record.id,
        record_kind: record.kind,
        issue_type: String(form.get("issueType") || "") as ContentReportIssueType,
        note: String(form.get("note") || "")
      });
      reportDialog.close();
      reportForm.reset();
      const original = reportButton.textContent || appContent.library.reportIssue;
      reportButton.textContent = appContent.library.reportIssueSent;
      window.setTimeout(() => { reportButton.textContent = original; }, 1400);
    } catch (error) {
      if (reportStatus) reportStatus.textContent = error instanceof Error ? error.message : appContent.library.reportIssueFailed;
    } finally {
      submit?.removeAttribute("disabled");
    }
  });
  if (!copyable) return;
  detail.querySelectorAll<HTMLButtonElement>(".clause-copy").forEach((button) => {
    button.addEventListener("click", async () => {
      const original = button.textContent || "";
      try {
        const payload = await view!.options.copyClause(record.id, button.dataset.format as "text" | "markdown");
        await navigator.clipboard.writeText(payload.content);
        button.textContent = appContent.library.copied;
      } catch {
        button.textContent = appContent.library.copyFailed;
      }
      window.setTimeout(() => { button.textContent = original; }, 1400);
    });
  });
  detail.querySelector<HTMLButtonElement>(".clause-screen-grab")?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const original = button.textContent || "";
    button.disabled = true;
    try {
      const detailAtStart = detailVersion;
      const subviewAtStart = subviewVersion;
      const subview = subviewPanel();
      const subviewId = subview && !subview.hidden ? subview.querySelector<HTMLElement>("[data-subview-record-id]")?.dataset.subviewRecordId : undefined;
      // Only the records the reader actually chose. The figures and tables a
      // clause renders inline are in the picture too, but the server derives
      // those from these roots — the stamp says what was captured, so it must
      // not be assembled from anything the client can edit.
      const payload = await view!.options.screenGrab([record.id, ...(subviewId ? [subviewId] : [])]);
      const blob = await renderScreenGrab(detail, subview && !subview.hidden ? subview : null, payload.stamp);
      // The stamp and the audit event name the records read above, but capture
      // is slow enough to navigate during. If either pane moved meanwhile the
      // image no longer matches its own provenance, so it is discarded rather
      // than handed over mis-stamped.
      if (detailVersion !== detailAtStart || subviewVersion !== subviewAtStart) throw new Error("Record changed during capture");
      await writeScreenGrab(blob);
      button.textContent = appContent.library.screenGrabbed;
    } catch {
      button.textContent = appContent.library.screenGrabFailed;
    } finally {
      button.disabled = false;
      window.setTimeout(() => { button.textContent = original; }, 1400);
    }
  });
}

function subviewPanel(): HTMLElement | null {
  return view?.root.querySelector<HTMLElement>(".reference-subview") || null;
}

function subviewSelection(): LibrarySelection | null {
  return subviewHistory[subviewIndex] || null;
}

function subviewRecordName(selection: LibrarySelection): string {
  const record = view?.byId.get(selection.id);
  if (!record) return appContent.library.loadingDetail;
  const title = assetKind(record.kind)
    ? captionWithoutIdentifier(record.number, record.title)
    : record.title || "";
  return `${record.number}${title ? ` ${title}` : ""}`;
}

function subviewFrameMarkup(selection: LibrarySelection, name: string, content: string): string {
  const copy = appContent.library;
  return `<header class="reference-subview-bar">
    <button class="secondary" type="button" data-subview-back aria-label="${copy.subviewBack}" ${subviewIndex > 0 ? "" : "disabled"}>${copy.subviewBack}</button>
    <button class="secondary" type="button" data-subview-forward aria-label="${copy.subviewForward}" ${subviewIndex < subviewHistory.length - 1 ? "" : "disabled"}>${copy.subviewForward}</button>
    <strong class="reference-subview-name" aria-live="polite">${escapeHtml(name)}</strong>
    <button type="button" data-subview-main>${copy.makeSubviewMain}</button>
    <button class="secondary" type="button" data-subview-close aria-label="${copy.closeSubview}">${copy.closeSubview}</button>
  </header><article class="reference-subview-content" data-subview-record-id="${escapeHtml(selection.id)}">${content}</article>`;
}

function bindSubviewBar(panel: HTMLElement): void {
  panel.querySelector<HTMLButtonElement>("[data-subview-back]")?.addEventListener("click", () => {
    if (subviewIndex <= 0) return;
    subviewIndex -= 1;
    void renderCurrentSubview();
    panel.focus({ preventScroll: true });
  });
  panel.querySelector<HTMLButtonElement>("[data-subview-forward]")?.addEventListener("click", () => {
    if (subviewIndex >= subviewHistory.length - 1) return;
    subviewIndex += 1;
    void renderCurrentSubview();
    panel.focus({ preventScroll: true });
  });
  panel.querySelector<HTMLButtonElement>("[data-subview-main]")?.addEventListener("click", () => {
    const selection = subviewSelection();
    if (!selection || !view) return;
    const detail = view.root.querySelector<HTMLElement>(".clause-detail");
    closeSubview(false);
    detail?.focus({ preventScroll: true });
    void select(selection, true);
  });
  panel.querySelector<HTMLButtonElement>("[data-subview-close]")?.addEventListener("click", () => closeSubview(true));
}

async function renderCurrentSubview(): Promise<void> {
  const selection = subviewSelection();
  const panel = subviewPanel();
  if (!selection || !panel || !view) return;
  const root = view.root;
  const version = ++subviewVersion;
  clearAssetImageBindings(panel);
  panel.hidden = false;
  panel.innerHTML = subviewFrameMarkup(selection, subviewRecordName(selection), loaderMarkup("thinking", appContent.library.loadingDetail));
  bindSubviewBar(panel);
  panel.scrollTop = 0;

  const stale = () => version !== subviewVersion || !view || view.root !== root || !document.body.contains(root) || panel.hidden;
  try {
    if (selection.kind === "asset") {
      const asset = await view.options.getAsset(selection.id);
      if (stale()) return;
      const caption = captionWithoutIdentifier(asset.number, asset.caption);
      const name = `${asset.number}${caption ? ` ${caption}` : ""}`;
      panel.innerHTML = subviewFrameMarkup(selection, name, assetDetailMarkup(asset, "subview"));
      bindSubviewBar(panel);
      bindDetail(panel, { id: asset.id, kind: asset.kind, number: asset.number, title: caption }, false, "subview");
    } else {
      const clause = await view.options.getClause(selection.id);
      if (stale()) return;
      const name = `${clause.clauseNumber}${clause.title ? ` ${clause.title}` : ""}`;
      panel.innerHTML = subviewFrameMarkup(selection, name, clauseDetailMarkup(clause, "subview"));
      linkifyClauseBody(panel, clause);
      bindSubviewBar(panel);
      bindDetail(panel, { id: clause.id, kind: "clause", number: clause.clauseNumber, title: clause.title }, false, "subview");
    }
  } catch (error) {
    if (stale()) return;
    const message = error instanceof Error ? error.message : appContent.library.detailFailed;
    panel.innerHTML = subviewFrameMarkup(selection, subviewRecordName(selection), `<p class="library-pointer">${escapeHtml(message)}</p>`);
    bindSubviewBar(panel);
  }
}

function openSubview(selection: LibrarySelection, returnFocus?: HTMLElement): void {
  const panel = subviewPanel();
  if (!panel || !view || !isMounted()) return;
  if (panel.hidden) {
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    subviewReturnFocus = returnFocus || active;
    subviewMainScrollTop = view.root.querySelector<HTMLElement>(".clause-detail")?.scrollTop || 0;
    subviewHistory = [selection];
    subviewIndex = 0;
  } else {
    subviewHistory = subviewHistory.slice(0, subviewIndex + 1);
    subviewHistory.push(selection);
    subviewIndex = subviewHistory.length - 1;
  }
  void renderCurrentSubview();
  panel.focus();
}

function closeSubview(restoreFocus: boolean): void {
  const panel = subviewPanel();
  if (!panel || panel.hidden) return;
  subviewVersion += 1;
  clearAssetImageBindings(panel);
  panel.hidden = true;
  panel.innerHTML = "";
  subviewHistory = [];
  subviewIndex = -1;
  const focusTarget = subviewReturnFocus;
  subviewReturnFocus = null;
  if (!restoreFocus || !view) return;
  const detail = view.root.querySelector<HTMLElement>(".clause-detail");
  // Focusing an element inside an overflow container scrolls that container, so
  // a plain focus() here would fight the restore below and show as a jump. The
  // saved position is the authority on where the main record goes back to.
  if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
  else detail?.focus({ preventScroll: true });
  if (detail) detail.scrollTop = subviewMainScrollTop;
}

// ---------------------------------------------------------------- selection

async function select(selection: LibrarySelection, updateLocation: boolean, revealGroup = true): Promise<void> {
  if (!view) return;
  closeSubview(false);
  view.selectedId = selection.id;
  // Reveal the group holding the target — arrows and deep links can land in a
  // collapsed group. This only ever adds, so nothing the reader opened closes.
  // Any change to the expansion set has to be redrawn, or the set and the DOM
  // drift apart and the group pops open later on an unrelated filter change.
  const record = view.byId.get(selection.id);
  // Every navigation to a specific record runs through here — route entry,
  // related and child links, cross-references, "make this the main clause",
  // arrows — so this is the one place the destination can be guaranteed
  // visible. Clearing runs before the expansion check, because it changes what
  // counts as visible. Must stay synchronous: callers read the cleared state on
  // the next line.
  const revealed = revealGroup ? revealRecordInMenu(record) : false;
  const wasOpen = Boolean(record?.group && displayedGroups().has(record.group));
  // Only reveal a group that is actually closed. Marking one that automatic
  // expansion already opened would turn it into a manual override, and it would
  // then stop responding to the automatic rule — staying open after a chip
  // change pushed it past the threshold.
  if (revealGroup && record?.group && !wasOpen) {
    view.expanded.add(record.group);
    if (view.query.trim().length >= 2) view.manuallyToggled.add(record.group);
  }
  const expanding = revealed || Boolean(revealGroup && record?.group && !wasOpen);
  const offscreen = !view.root.querySelector(`[data-record-id="${CSS.escape(selection.id)}"]`);
  if (expanding || offscreen) renderMenu(); else highlightSelection();
  if (updateLocation) view.options.setLocation(selection);

  const detail = view.root.querySelector<HTMLElement>(".clause-detail");
  if (!detail) return;
  const version = ++detailVersion;
  clearAssetImageBindings(detail);
  detail.innerHTML = loaderMarkup("thinking", appContent.library.loadingDetail);

  try {
    if (selection.kind === "asset") {
      const asset = await view.options.getAsset(selection.id);
      if (version !== detailVersion || !view) return;
      detail.innerHTML = assetDetailMarkup(asset);
      bindDetail(detail, { id: asset.id, kind: asset.kind, number: asset.number, title: captionWithoutIdentifier(asset.number, asset.caption) }, false, "main");
    } else {
      const clause = await view.options.getClause(selection.id);
      if (version !== detailVersion || !view) return;
      detail.innerHTML = clauseDetailMarkup(clause);
      linkifyClauseBody(detail, clause);
      bindDetail(detail, { id: clause.id, kind: "clause", number: clause.clauseNumber, title: clause.title }, true, "main");
    }
    detail.scrollTop = 0;
  } catch (error) {
    if (version !== detailVersion) return;
    detail.textContent = error instanceof Error ? error.message : appContent.library.detailFailed;
  }
}

function applyFilter(): void {
  if (!view) return;
  renderMenu();
  // The arrows walk the filtered list, so they change when the filter does.
  const pagination = view.root.querySelector<HTMLElement>(".clause-pagination");
  if (pagination) {
    pagination.outerHTML = arrowsMarkup();
    view.root.querySelectorAll<HTMLButtonElement>(".clause-pagination [data-record-id]").forEach((button) => {
      button.addEventListener("click", () => {
        const target = view!.byId.get(button.dataset.recordId || "");
        if (target) void select(selectionFor(target), true);
      });
    });
  }
}

function scheduleFilter(query: string, delay = 250): void {
  if (!view) return;
  window.clearTimeout(filterTimer);
  const requestVersion = ++filterVersion;
  const scheduledFor = view.root;
  filterTimer = window.setTimeout(() => {
    const stale = () => requestVersion !== filterVersion || !view || view.root !== scheduledFor;
    void view!.options.filterNavigation(query).then(({ ids }) => {
      if (stale()) return;
      view!.matchedIds = new Set(ids);
      view!.filterError = null;
      applyFilter();
    }).catch((error: unknown) => {
      if (stale()) return;
      view!.matchedIds = new Set<string>();
      view!.filterError = error instanceof Error ? error.message : appContent.library.filterFailed;
      applyFilter();
    });
  }, delay);
}

function bindControls(): void {
  if (!view) return;
  libraryKeydownController?.abort();
  libraryKeydownController = new AbortController();
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || activeLightbox || view?.root.querySelector(".report-issue-dialog[open]") || subviewPanel()?.hidden !== false) return;
    event.preventDefault();
    closeSubview(true);
  }, { signal: libraryKeydownController.signal });
  const input = view.root.querySelector<HTMLInputElement>("#clauseFilter");
  input?.addEventListener("input", () => {
    const query = input.value.trim();
    if (query === view!.query) return;
    view!.manuallyToggled = manualOverridesAfterQueryChange(view!.query, query, view!.manuallyToggled);
    view!.query = query;
    window.clearTimeout(filterTimer);
    filterVersion += 1;
    view!.matchedIds = null;
    view!.filterError = null;
    if (query.length < 2) {
      // Only emptying the box collapses the menu. Automatic expansions live
      // outside `expanded` and fall away on their own, so this exists solely to
      // close groups the reader opened during the search — and typing the first
      // character of a new search is not clearing the filter.
      if (query.length === 0) view!.expanded.clear();
      applyFilter();
      return;
    }
    applyFilter();
    // Scoped to the view that scheduled it: leaving and quickly re-entering
    // must not let an old query update the freshly mounted menu.
    scheduleFilter(query);
  });

  view.root.querySelectorAll<HTMLButtonElement>(".library-chip").forEach((button) => {
    button.addEventListener("click", () => {
      view!.chip = (button.dataset.chip as ChipFilter) || "all";
      view!.root.querySelectorAll<HTMLButtonElement>(".library-chip").forEach((chip) => {
        chip.setAttribute("aria-pressed", String(chip.dataset.chip === view!.chip));
      });
      applyFilter();
    });
  });
}

export function renderLibraryScreen(options: LibraryScreenOptions): void {
  // Re-entering the library — from the sidebar, a search result, a deep link —
  // must not throw the menu away if it is already on screen.
  if (isMounted()) {
    view!.options = options;
    // Re-entering from the sidebar must not assume the current record is a
    // clause — it may be a figure, whose id would 404 against the clause
    // endpoint. With nothing selected there is nothing to re-open.
    // Either queue or select — never both. Selecting while records are still
    // loading fetches the record twice and leaves a stale route target for the
    // in-flight callback to apply over a newer choice the reader has since made.
    if (!view!.navigationLoaded) {
      if (options.selected) view!.pendingSelection = options.selected;
      return;
    }
    // Records are here, so no callback remains to consume a queued selection.
    view!.pendingSelection = null;
    const current = view!.selectedId ? view!.byId.get(view!.selectedId) : undefined;
    const target = options.selected || (current ? selectionFor(current) : null);
    // select() clears any filter hiding the destination, so nothing extra here.
    if (target) void select(target, true);
    return;
  }

  options.renderWorkspace(shellMarkup());
  options.bindWorkspaceNavigation();
  const root = document.querySelector<HTMLElement>(".library-workspace");
  if (!root) return;

  // Invalidate any filter work still in flight from a previous mount.
  window.clearTimeout(filterTimer);
  filterVersion += 1;
  const restored = menuSessionState;
  view = {
    root,
    options,
    records: [],
    groups: [],
    byId: new Map(),
    expanded: new Set(restored?.expanded),
    manuallyToggled: new Set(restored?.manuallyToggled),
    chip: restored?.chip || "all",
    query: restored?.query || "",
    matchedIds: restored?.matchedIds ? new Set(restored.matchedIds) : null,
    filterError: null,
    selectedId: restored?.selectedId || null,
    listScrollTop: restored?.listScrollTop || 0,
    menuRendered: false,
    pendingSelection: options.selected ?? null,
    navigationLoaded: false
  };
  const input = root.querySelector<HTMLInputElement>("#clauseFilter");
  if (input) input.value = view.query;
  root.querySelectorAll<HTMLButtonElement>(".library-chip").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.chip === view!.chip));
  });
  bindControls();

  void options.listNavigation().then(({ records, groups }) => {
    // Comparing roots catches a re-mount; containment also covers a root that
    // the application shell has already detached. Without both checks, a slow
    // /api/navigation could write a library hash onto the next screen.
    if (!view || view.root !== root || !document.body.contains(root)) return;
    view.navigationLoaded = true;
    view.records = records;
    view.groups = groups;
    view.byId = new Map(records.map((record) => [record.id, record]));
    const groupKeys = new Set(groups.map((group) => group.key));
    view.expanded = new Set([...view.expanded].filter((key) => groupKeys.has(key)));
    view.manuallyToggled = new Set([...view.manuallyToggled].filter((key) => groupKeys.has(key)));
    let refreshRestoredFilter = false;
    if (view.matchedIds) {
      const restoredMatchCount = view.matchedIds.size;
      view.matchedIds = new Set([...view.matchedIds].filter((id) => view!.byId.has(id)));
      refreshRestoredFilter = view.matchedIds.size !== restoredMatchCount;
      if (refreshRestoredFilter) view.matchedIds = null;
    }
    // Use the newest request, not the one this call closed over: re-entering
    // while /api/navigation is in flight must not be overridden by the target
    // the original invocation started with.
    const requestedSelection = view.pendingSelection;
    view.pendingSelection = null;
    const requested = requestedSelection ? view.byId.get(requestedSelection.id) : undefined;
    if (revealRecordInMenu(requested)) refreshRestoredFilter = false;
    const remembered = !requestedSelection && view.selectedId ? view.byId.get(view.selectedId) : undefined;
    const initialRecord = requested || remembered || records[0] || null;
    const initial = initialRecord ? selectionFor(initialRecord) : null;
    renderMenu();
    // Always write the location: setLocation no-ops when the hash already
    // matches, and arriving from a search result needs a shareable link too.
    if (initial) void select(initial, true, Boolean(requested));
    else {
      const detail = root.querySelector<HTMLElement>(".clause-detail");
      if (detail) detail.textContent = appContent.library.empty;
    }
    if (view.query.length >= 2 && (view.matchedIds === null || refreshRestoredFilter)) scheduleFilter(view.query, 0);
  }).catch((error) => {
    if (!view || view.root !== root) return;
    // Mark it resolved even on failure, or a queued selection would sit
    // forever and every later re-entry would silently do nothing.
    view.navigationLoaded = true;
    const queued = view.pendingSelection;
    view.pendingSelection = null;
    // The menu could not load, but select() fetches the record's detail
    // independently, so a queued deep link still gets its clause instead of
    // nothing. Its synchronous part repaints the menu, so the error message is
    // written afterwards and survives.
    if (queued) void select(queued, true);
    const container = root.querySelector<HTMLElement>(".clause-list-items");
    if (container) container.textContent = error instanceof Error ? error.message : appContent.library.navigationFailed;
  });
}
